import { useCallback, useEffect, useRef, useState } from 'react';
import PricingCard from '../home/PricingSection/PricingCard';

const VISIBLE_PLAN_COUNT = 3;
const MIN_CARD_WIDTH = 280;

function getVisiblePlanCount(containerWidth) {
  if (containerWidth < 720) return 1;
  if (containerWidth < 1100) return 2;
  return VISIBLE_PLAN_COUNT;
}

const LAYOUT = {
  default: { gap: 24, cardHeight: 545, navSize: 'h-10 w-10', chevron: 'h-5 w-5', padY: 'py-6', padX: 'px-3' },
  compact: { gap: 16, cardHeight: 450, navSize: 'h-9 w-9', chevron: 'h-4 w-4', padY: 'py-5', padX: 'px-2' },
};

function NavButton({ active, onClick, label, children, sizeClass }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!active}
      aria-label={label}
      aria-disabled={!active}
      className={`flex items-center justify-center rounded-full border-2 transition ${sizeClass} ${
        active
          ? 'border-[#9369CC] bg-white text-[#9369CC] shadow-md hover:bg-[#9369CC]/10'
          : 'cursor-default border-gray-200 bg-gray-50 text-gray-300 opacity-50'
      }`}
    >
      {children}
    </button>
  );
}

function getCardWidth(containerWidth, gap, visibleCount) {
  const count = visibleCount || getVisiblePlanCount(containerWidth);
  return Math.max(
    MIN_CARD_WIDTH,
    Math.floor((containerWidth - gap * (count - 1)) / count)
  );
}

export default function PricingPlansCarousel({
  plans,
  loading,
  startPayment,
  payingPlanId,
  isPlanSubscribed,
  className = 'mt-6',
  emptyMessage = 'No subscription plans available right now.',
  compact = false,
}) {
  const layout = compact ? LAYOUT.compact : LAYOUT.default;
  const { gap: planGap, cardHeight, navSize, chevron: chevronSize, padY, padX } = layout;

  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const [visibleCount, setVisibleCount] = useState(VISIBLE_PLAN_COUNT);

  const maxIndex = Math.max(0, plans.length - visibleCount);
  const showNav = plans.length > visibleCount;
  const canScrollPrev = activeIndex > 0;
  const canScrollNext = activeIndex < maxIndex;
  const fitsContainer = !showNav;

  const measureLayout = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const nextVisibleCount = getVisiblePlanCount(el.clientWidth);
    setVisibleCount(nextVisibleCount);
    setCardWidth(getCardWidth(el.clientWidth, planGap, nextVisibleCount));
  }, [planGap]);

  const syncIndexFromScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const step = getCardWidth(el.clientWidth, planGap, visibleCount) + planGap;
    if (step <= 0) return;

    const nextMaxIndex = Math.max(0, plans.length - getVisiblePlanCount(el.clientWidth));
    const index = Math.round(el.scrollLeft / step);
    setActiveIndex(Math.min(nextMaxIndex, Math.max(0, index)));
  }, [planGap, plans.length, visibleCount]);

  useEffect(() => {
    setActiveIndex(0);
    const el = scrollRef.current;
    if (el) el.scrollLeft = 0;

    measureLayout();
    const timer = window.setTimeout(measureLayout, 100);
    return () => window.clearTimeout(timer);
  }, [plans, measureLayout]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    measureLayout();
    const observer = new ResizeObserver(measureLayout);
    observer.observe(el);
    window.addEventListener('resize', measureLayout);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measureLayout);
    };
  }, [measureLayout, plans]);

  useEffect(() => {
    if (activeIndex > maxIndex) {
      setActiveIndex(maxIndex);
    }
  }, [activeIndex, maxIndex]);

  const scrollPlans = (direction) => {
    const el = scrollRef.current;
    if (!el) return;

    const nextIndex =
      direction === 'next'
        ? Math.min(activeIndex + 1, maxIndex)
        : Math.max(activeIndex - 1, 0);

    if (nextIndex === activeIndex) return;

    const step = (cardWidth || getCardWidth(el.clientWidth, planGap, visibleCount)) + planGap;

    setActiveIndex(nextIndex);
    el.scrollTo({
      left: nextIndex * step,
      behavior: 'smooth',
    });
  };

  const cardStyle = cardWidth
    ? { width: cardWidth, minWidth: cardWidth, maxWidth: cardWidth, height: cardHeight, flex: '0 0 auto' }
    : { height: cardHeight, flex: '0 0 auto' };

  const gapClass = compact ? 'gap-4' : 'gap-6';

  const chevronLeft = (
    <svg className={chevronSize} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  );

  const chevronRight = (
    <svg className={chevronSize} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );

  if (loading) {
    return (
      <div className={`w-full min-w-0 overflow-visible ${className}`}>
        <div className={`${padX}`}>
          <div
            ref={scrollRef}
            className={`flex ${gapClass} overflow-x-auto overflow-y-visible ${padY} [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
          >
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                data-plan-card
                style={cardStyle}
                className="animate-pulse rounded-2xl bg-white/70"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!plans.length) {
    return <p className={`text-center text-base font-medium text-[#666666] ${className}`}>{emptyMessage}</p>;
  }

  return (
    <div className={`relative w-full min-w-0 overflow-visible ${className}`}>
      {showNav && (
        <div className="mb-3 flex justify-center gap-2 sm:justify-end">
          <NavButton
            active={canScrollPrev}
            onClick={() => scrollPlans('prev')}
            label="Previous plans"
            sizeClass={navSize}
          >
            {chevronLeft}
          </NavButton>
          <NavButton
            active={canScrollNext}
            onClick={() => scrollPlans('next')}
            label="Next plans"
            sizeClass={navSize}
          >
            {chevronRight}
          </NavButton>
        </div>
      )}

      <div className={`w-full min-w-0 overflow-visible ${padX}`}>
        <div
          ref={scrollRef}
          onScroll={syncIndexFromScroll}
          className={`flex w-full min-w-0 ${gapClass} overflow-x-auto overflow-y-visible ${padY} [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
            fitsContainer ? 'justify-center' : ''
          } ${showNav ? 'snap-x snap-mandatory' : ''}`}
        >
          {plans.map((plan) => (
            <div
              key={plan.id}
              data-plan-card
              style={cardStyle}
              className={`flex min-w-0 overflow-visible ${showNav ? 'snap-start' : ''}`}
            >
              <PricingCard
                plan={plan}
                compact={compact}
                onStartLearning={startPayment}
                paying={payingPlanId === Number(plan.id)}
                isSubscribed={isPlanSubscribed(plan)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
